export interface AgentsRouteSearch {
  readonly assistantNewThreadKey?: string;
  readonly assistantUserId?: string;
  readonly assistantWorkspaceId?: string;
  readonly handoffArtifact?: AgentAuthoringDraftIntentArtifact;
  readonly handoffDraft?: AgentAuthoringDraftState;
  readonly handoffPrompt?: string;
  readonly handoffReason?: string;
  readonly handoffSource?: "user_assistant";
  readonly portfolioConstructionPrompt?: string;
  readonly portfolioConstructionThreadId?: string;
}

type AgentAuthoringDraftIntentVariableRole =
  | "sold_asset"
  | "bought_asset"
  | "funding_asset"
  | "destination_asset"
  | "trigger_asset"
  | "portfolio"
  | "account"
  | "amount"
  | "schedule"
  | "condition";

interface AgentAuthoringDraftIntentVariableHint {
  readonly chainHint: string | null;
  readonly rawText: string;
  readonly role: AgentAuthoringDraftIntentVariableRole;
  readonly symbolOrAddressOrNameHint: string | null;
  readonly variableId: string;
}

interface AgentAuthoringDraftExtractedIntent {
  readonly amount: {
    readonly kind: "usd" | "token_quantity" | "percent" | "unspecified";
    readonly rawText: string;
    readonly unitHint?: string | null;
    readonly valueHint?: string | null;
  } | null;
  readonly actions?: readonly {
    readonly amount: AgentAuthoringDraftExtractedIntent["amount"];
    readonly kind:
      | "buy"
      | "sell"
      | "swap"
      | "transfer"
      | "rebalance"
      | "alert"
      | "wait"
      | "notify"
      | "unknown";
    readonly rawText: string;
    readonly targetVariableId: string | null;
  }[];
  readonly condition: {
    readonly comparator:
      | "gt"
      | "gte"
      | "lt"
      | "lte"
      | "eq"
      | "pct_drop_gte"
      | "pct_rise_gte"
      | "unknown";
    readonly metric:
      | "price_usd"
      | "pct_change"
      | "portfolio_metric"
      | "unknown";
    readonly rawText: string;
    readonly subjectVariableId?: string | null;
    readonly thresholdRawText?: string | null;
    readonly thresholdValueHint?: string | null;
  } | null;
  readonly confidence: "high" | "medium" | "low";
  readonly notes: readonly string[];
  readonly parser: "llm";
  readonly plannerPrompt: string | null;
  readonly schedule: {
    readonly cadenceHint?: string | null;
    readonly rawText: string;
  } | null;
  readonly variables: readonly AgentAuthoringDraftIntentVariableHint[];
}

export interface AgentAuthoringDraftIntentArtifact {
  readonly actionFamily:
    | "construct_portfolio"
    | "buy"
    | "sell"
    | "swap"
    | "transfer"
    | "rebalance"
    | "alert"
    | "multi_action"
    | "unknown";
  readonly candidatePortfolioId?: string | null;
  readonly intentFamily:
    | "portfolio_construction"
    | "portfolio_trade"
    | "portfolio_transfer"
    | "portfolio_rebalance"
    | "portfolio_monitor"
    | "multi_step_trade"
    | "workflow_unknown";
  readonly missingSlots: readonly (
    | "amount"
    | "portfolio"
    | "asset"
    | "source_asset"
    | "schedule"
    | "trigger_condition"
  )[];
  readonly scheduleHints: {
    readonly hasAbsoluteTime: boolean;
    readonly hasRecurringCadence: boolean;
    readonly summary?: string | null;
  };
  readonly triggerHints: {
    readonly hasTriggerCondition: boolean;
    readonly summary?: string | null;
  };
  readonly draftContext?: {
    readonly portfolioContextSummary?: string | null;
    readonly previousUserPrompts?: readonly string[];
    readonly recentResearchSummary?: string | null;
  } | null;
  readonly extractedIntent?: AgentAuthoringDraftExtractedIntent | null;
  readonly plannerPrompt?: string | null;
  readonly targetPortfolioIds?: readonly string[];
  readonly userPhrasingSummary: string;
}

export interface AgentAuthoringDraftState {
  readonly committedWorkflowId?: string | null;
  readonly currentQuestionKind?: string | null;
  readonly draftId: string;
  readonly missingFieldIds: readonly string[];
  readonly status:
    | "cancelled"
    | "committed"
    | "draft"
    | "needs_confirmation"
    | "ready_to_confirm";
  readonly updatedAt: string;
}

const readOptionalTrimmedString = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const isDraftIntentVariableRole = (
  value: unknown,
): value is AgentAuthoringDraftIntentVariableRole =>
  value === "sold_asset" ||
  value === "bought_asset" ||
  value === "funding_asset" ||
  value === "destination_asset" ||
  value === "trigger_asset" ||
  value === "portfolio" ||
  value === "account" ||
  value === "amount" ||
  value === "schedule" ||
  value === "condition";

const normalizeExtractedAmount = (
  value: unknown,
): AgentAuthoringDraftExtractedIntent["amount"] => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const record = value as Record<string, unknown>;
  const rawText = readOptionalTrimmedString(record.rawText);
  const kind = record.kind;
  if (
    !rawText ||
    (kind !== "usd" &&
      kind !== "token_quantity" &&
      kind !== "percent" &&
      kind !== "unspecified")
  ) {
    return null;
  }

  return {
    kind,
    rawText,
    unitHint: readOptionalTrimmedString(record.unitHint) ?? null,
    valueHint: readOptionalTrimmedString(record.valueHint) ?? null,
  };
};

const normalizeExtractedCondition = (
  value: unknown,
): AgentAuthoringDraftExtractedIntent["condition"] => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const record = value as Record<string, unknown>;
  const comparator = record.comparator;
  const metric = record.metric;
  const rawText = readOptionalTrimmedString(record.rawText);
  if (
    !rawText ||
    (comparator !== "gt" &&
      comparator !== "gte" &&
      comparator !== "lt" &&
      comparator !== "lte" &&
      comparator !== "eq" &&
      comparator !== "pct_drop_gte" &&
      comparator !== "pct_rise_gte" &&
      comparator !== "unknown") ||
    (metric !== "price_usd" &&
      metric !== "pct_change" &&
      metric !== "portfolio_metric" &&
      metric !== "unknown")
  ) {
    return null;
  }

  return {
    comparator,
    metric,
    rawText,
    subjectVariableId:
      readOptionalTrimmedString(record.subjectVariableId) ?? null,
    thresholdRawText:
      readOptionalTrimmedString(record.thresholdRawText) ?? null,
    thresholdValueHint:
      readOptionalTrimmedString(record.thresholdValueHint) ?? null,
  };
};

const normalizeExtractedSchedule = (
  value: unknown,
): AgentAuthoringDraftExtractedIntent["schedule"] => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const record = value as Record<string, unknown>;
  const rawText = readOptionalTrimmedString(record.rawText);
  if (!rawText) {
    return null;
  }

  return {
    cadenceHint: readOptionalTrimmedString(record.cadenceHint) ?? null,
    rawText,
  };
};

const normalizeExtractedIntent = (
  value: unknown,
): AgentAuthoringDraftExtractedIntent | undefined => {
  if (!value || typeof value !== "object") {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  const confidence = record.confidence;
  if (
    record.parser !== "llm" ||
    (confidence !== "high" &&
      confidence !== "medium" &&
      confidence !== "low") ||
    !Array.isArray(record.variables)
  ) {
    return undefined;
  }

  const variables = record.variables.flatMap((variable) => {
      if (!variable || typeof variable !== "object") {
        return [];
      }

      const variableRecord = variable as Record<string, unknown>;
      const variableId = readOptionalTrimmedString(variableRecord.variableId);
      const rawText = readOptionalTrimmedString(variableRecord.rawText);
      const role = variableRecord.role;
      if (!variableId || !rawText || !isDraftIntentVariableRole(role)) {
        return [];
      }

      return [
        {
          chainHint: readOptionalTrimmedString(variableRecord.chainHint) ?? null,
          rawText,
          role,
          symbolOrAddressOrNameHint:
            readOptionalTrimmedString(variableRecord.symbolOrAddressOrNameHint) ??
            null,
          variableId,
        } satisfies AgentAuthoringDraftIntentVariableHint,
      ];
    });
  const actions = Array.isArray(record.actions)
    ? record.actions.flatMap((action) => {
          if (!action || typeof action !== "object") {
            return [];
          }

          const actionRecord = action as Record<string, unknown>;
          const kind = actionRecord.kind;
          const rawText = readOptionalTrimmedString(actionRecord.rawText);
          if (
            !rawText ||
            (kind !== "buy" &&
              kind !== "sell" &&
              kind !== "swap" &&
              kind !== "transfer" &&
              kind !== "rebalance" &&
              kind !== "alert" &&
              kind !== "wait" &&
              kind !== "notify" &&
              kind !== "unknown")
          ) {
            return [];
          }

          return [
            {
              amount: normalizeExtractedAmount(actionRecord.amount),
              kind,
              rawText,
              targetVariableId:
                readOptionalTrimmedString(actionRecord.targetVariableId) ?? null,
            } satisfies NonNullable<
              AgentAuthoringDraftExtractedIntent["actions"]
            >[number],
          ];
        })
    : undefined;

  return {
    amount: normalizeExtractedAmount(record.amount),
    ...(actions ? { actions } : {}),
    condition: normalizeExtractedCondition(record.condition),
    confidence,
    notes: Array.isArray(record.notes)
      ? record.notes.filter(
          (note): note is string =>
            typeof note === "string" && note.trim().length > 0,
        )
      : [],
    parser: "llm",
    plannerPrompt: readOptionalTrimmedString(record.plannerPrompt) ?? null,
    schedule: normalizeExtractedSchedule(record.schedule),
    variables,
  };
};

const normalizeDraftIntentArtifact = (
  value: unknown,
): AgentAuthoringDraftIntentArtifact | undefined => {
  const input =
    typeof value === "string"
      ? (() => {
          try {
            return JSON.parse(value) as unknown;
          } catch {
            return null;
          }
        })()
      : value;

  if (!input || typeof input !== "object") {
    return undefined;
  }

  const record = input as Record<string, unknown>;
  const actionFamily = record.actionFamily;
  const intentFamily = record.intentFamily;
  const userPhrasingSummary = readOptionalTrimmedString(
    record.userPhrasingSummary,
  );
  const scheduleHints =
    record.scheduleHints && typeof record.scheduleHints === "object"
      ? (record.scheduleHints as Record<string, unknown>)
      : null;
  const triggerHints =
    record.triggerHints && typeof record.triggerHints === "object"
      ? (record.triggerHints as Record<string, unknown>)
      : null;
  const draftContext =
    record.draftContext && typeof record.draftContext === "object"
      ? (record.draftContext as Record<string, unknown>)
      : null;
  const missingSlots = Array.isArray(record.missingSlots)
    ? record.missingSlots.filter(
        (
          slot,
        ): slot is AgentAuthoringDraftIntentArtifact["missingSlots"][number] =>
          slot === "amount" ||
          slot === "portfolio" ||
          slot === "asset" ||
          slot === "source_asset" ||
          slot === "schedule" ||
          slot === "trigger_condition",
      )
    : [];
  const candidatePortfolioId = readOptionalTrimmedString(
    record.candidatePortfolioId,
  );
  const targetPortfolioIds = Array.isArray(record.targetPortfolioIds)
    ? record.targetPortfolioIds
        .map(readOptionalTrimmedString)
        .filter((value): value is string => value !== undefined)
    : [];
  const extractedIntent = normalizeExtractedIntent(record.extractedIntent);
  const plannerPrompt = readOptionalTrimmedString(record.plannerPrompt);

  if (
    (actionFamily !== "buy" &&
      actionFamily !== "construct_portfolio" &&
      actionFamily !== "sell" &&
      actionFamily !== "swap" &&
      actionFamily !== "transfer" &&
      actionFamily !== "rebalance" &&
      actionFamily !== "alert" &&
      actionFamily !== "multi_action" &&
      actionFamily !== "unknown") ||
    (intentFamily !== "portfolio_trade" &&
      intentFamily !== "portfolio_construction" &&
      intentFamily !== "portfolio_transfer" &&
      intentFamily !== "portfolio_rebalance" &&
      intentFamily !== "portfolio_monitor" &&
      intentFamily !== "multi_step_trade" &&
      intentFamily !== "workflow_unknown") ||
    !userPhrasingSummary ||
    !scheduleHints ||
    !triggerHints ||
    typeof scheduleHints.hasAbsoluteTime !== "boolean" ||
    typeof scheduleHints.hasRecurringCadence !== "boolean" ||
    !(
      scheduleHints.summary === undefined ||
      scheduleHints.summary === null ||
      typeof scheduleHints.summary === "string"
    ) ||
    typeof triggerHints.hasTriggerCondition !== "boolean" ||
    !(
      triggerHints.summary === undefined ||
      triggerHints.summary === null ||
      typeof triggerHints.summary === "string"
    )
  ) {
    return undefined;
  }

  return {
    actionFamily,
    candidatePortfolioId: candidatePortfolioId ?? null,
    intentFamily,
    missingSlots,
    scheduleHints: {
      hasAbsoluteTime: scheduleHints.hasAbsoluteTime,
      hasRecurringCadence: scheduleHints.hasRecurringCadence,
      summary:
        typeof scheduleHints.summary === "string"
          ? scheduleHints.summary
          : null,
    },
    triggerHints: {
      hasTriggerCondition: triggerHints.hasTriggerCondition,
      summary:
        typeof triggerHints.summary === "string" ? triggerHints.summary : null,
    },
    draftContext:
      draftContext === null
        ? null
        : {
            portfolioContextSummary:
              typeof draftContext.portfolioContextSummary === "string"
                ? draftContext.portfolioContextSummary
                : null,
            previousUserPrompts: Array.isArray(draftContext.previousUserPrompts)
              ? draftContext.previousUserPrompts.filter(
                  (value): value is string =>
                    typeof value === "string" && value.trim().length > 0,
                )
              : [],
            recentResearchSummary:
              typeof draftContext.recentResearchSummary === "string"
                ? draftContext.recentResearchSummary
                : null,
    },
    ...(extractedIntent ? { extractedIntent } : {}),
    ...(plannerPrompt ? { plannerPrompt } : {}),
    ...(targetPortfolioIds.length > 0 ? { targetPortfolioIds } : {}),
    userPhrasingSummary,
  };
};

const normalizeDraftState = (
  value: unknown,
): AgentAuthoringDraftState | undefined => {
  const input =
    typeof value === "string"
      ? (() => {
          try {
            return JSON.parse(value) as unknown;
          } catch {
            return null;
          }
        })()
      : value;

  if (!input || typeof input !== "object") {
    return undefined;
  }

  const record = input as Record<string, unknown>;
  const draftId = readOptionalTrimmedString(record.draftId);
  const status = record.status;
  const updatedAt = readOptionalTrimmedString(record.updatedAt);
  const currentQuestionKind = readOptionalTrimmedString(
    record.currentQuestionKind,
  );
  const committedWorkflowId = readOptionalTrimmedString(
    record.committedWorkflowId,
  );
  const missingFieldIds = Array.isArray(record.missingFieldIds)
    ? record.missingFieldIds.filter(
        (value): value is string =>
          typeof value === "string" && value.trim().length > 0,
      )
    : [];

  if (
    !draftId ||
    !updatedAt ||
    (status !== "cancelled" &&
      status !== "committed" &&
      status !== "draft" &&
      status !== "needs_confirmation" &&
      status !== "ready_to_confirm")
  ) {
    return undefined;
  }

  return {
    committedWorkflowId: committedWorkflowId ?? null,
    currentQuestionKind: currentQuestionKind ?? null,
    draftId,
    missingFieldIds,
    status,
    updatedAt,
  };
};

export const toAgentsRouteSearch = (raw: unknown): AgentsRouteSearch => {
  if (!raw || typeof raw !== "object") {
    return {};
  }

  const input = raw as Record<string, unknown>;
  const handoffSource =
    input.handoffSource === "user_assistant" ? "user_assistant" : undefined;

  return {
    assistantNewThreadKey: readOptionalTrimmedString(
      input.assistantNewThreadKey,
    ),
    assistantUserId: readOptionalTrimmedString(input.assistantUserId),
    assistantWorkspaceId: readOptionalTrimmedString(
      input.assistantWorkspaceId,
    ),
    handoffArtifact: normalizeDraftIntentArtifact(input.handoffArtifact),
    handoffDraft: normalizeDraftState(input.handoffDraft),
    handoffPrompt: readOptionalTrimmedString(input.handoffPrompt),
    handoffReason: readOptionalTrimmedString(input.handoffReason),
    handoffSource,
    portfolioConstructionPrompt: readOptionalTrimmedString(
      input.portfolioConstructionPrompt,
    ),
    portfolioConstructionThreadId: readOptionalTrimmedString(
      input.portfolioConstructionThreadId,
    ),
  };
};

export const serializeAgentAuthoringDraftIntentArtifact = (
  artifact: AgentAuthoringDraftIntentArtifact,
): string => JSON.stringify(artifact);

export const serializeAgentAuthoringDraftState = (
  draft: AgentAuthoringDraftState,
): string => JSON.stringify(draft);

type AgentAuthoringRouteHrefParams = {
  assistantUserId?: string | null;
  assistantWorkspaceId?: string | null;
  handoffArtifact?: AgentAuthoringDraftIntentArtifact | null;
  handoffDraft?: AgentAuthoringDraftState | null;
  handoffPrompt?: string | null;
  handoffReason?: string | null;
  handoffSource?: "user_assistant" | null;
  portfolioConstructionPrompt?: string | null;
  portfolioConstructionThreadId?: string | null;
};

function buildAgentAuthoringRouteHref(
  basePath: "/agents",
  params: AgentAuthoringRouteHrefParams,
): string {
  const searchParams = new URLSearchParams();

  if (params.assistantUserId?.trim()) {
    searchParams.set("assistantUserId", params.assistantUserId.trim());
  }
  if (params.assistantWorkspaceId?.trim()) {
    searchParams.set(
      "assistantWorkspaceId",
      params.assistantWorkspaceId.trim(),
    );
  }
  if (params.handoffArtifact) {
    searchParams.set(
      "handoffArtifact",
      serializeAgentAuthoringDraftIntentArtifact(params.handoffArtifact),
    );
  }
  if (params.handoffDraft) {
    searchParams.set(
      "handoffDraft",
      serializeAgentAuthoringDraftState(params.handoffDraft),
    );
  }
  if (params.handoffPrompt?.trim()) {
    searchParams.set("handoffPrompt", params.handoffPrompt.trim());
  }
  if (params.handoffReason?.trim()) {
    searchParams.set("handoffReason", params.handoffReason.trim());
  }
  if (params.handoffSource === "user_assistant") {
    searchParams.set("handoffSource", "user_assistant");
  }
  if (params.portfolioConstructionPrompt?.trim()) {
    searchParams.set(
      "portfolioConstructionPrompt",
      params.portfolioConstructionPrompt.trim(),
    );
  }
  if (params.portfolioConstructionThreadId?.trim()) {
    searchParams.set(
      "portfolioConstructionThreadId",
      params.portfolioConstructionThreadId.trim(),
    );
  }
  const search = searchParams.toString();
  return search.length > 0 ? `${basePath}?${search}` : basePath;
}

export const buildAgentsRouteHref = (
  params: AgentAuthoringRouteHrefParams,
): string => buildAgentAuthoringRouteHref("/agents", params);
